import { useEffect, useState } from "react";
import { useOutletContext } from "react-router-dom";
import { Archive, RotateCcw } from "lucide-react";
import { api, formatApiError, CLASS_STYLE, CLASSIFICATION_LABELS, PRIORITY_LABELS } from "@/lib/api";
import { toast } from "sonner";

export default function ArchivedTasksPage() {
  const ctx = useOutletContext();
  const [tasks, setTasks] = useState(null);
  const [filters, setFilters] = useState({ q: "", classification: "", sprint: "", assignee: "" });
  const canAdmin = !!ctx.project.can_admin;

  const load = async () => {
    try {
      const params = {};
      if (filters.q) params.q = filters.q;
      if (filters.classification) params.classification = filters.classification;
      if (filters.sprint) params.sprint = filters.sprint;
      if (filters.assignee) params.assignee = filters.assignee;
      const { data } = await api.get(`/projects/${ctx.projectId}/archived-tasks`, { params });
      setTasks(data);
    } catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filters, ctx.refreshKey]);

  const restore = async (id, e) => {
    e.stopPropagation();
    try { await api.post(`/tasks/${id}/restore-archived`); toast.success("Tarefa restaurada ao Backlog."); load(); ctx.bump(); }
    catch (err) { toast.error(formatApiError(err)); }
  };
  const fmt = (d) => d ? new Date(d).toLocaleDateString("pt-BR") : "—";

  return (
    <div data-testid="archived-page">
      <div className="mb-4">
        <div className="text-xs font-bold uppercase tracking-widest text-slate-500">{ctx.project.workspace_name}</div>
        <h1 className="font-heading text-3xl sm:text-4xl font-semibold tracking-tight text-slate-900 mt-2 flex items-center gap-2"><Archive className="w-6 h-6 text-slate-400" /> Tarefas concluídas arquivadas</h1>
        <p className="text-slate-500 mt-1">Tarefas concluídas há mais de 7 dias, fora do quadro ativo. Dados e histórico preservados.</p>
      </div>

      <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-4 mb-4 flex flex-wrap gap-2" data-testid="archived-filters">
        <input value={filters.q} onChange={(e) => setFilters({ ...filters, q: e.target.value })} placeholder="Buscar..." data-testid="archived-search" className="h-9 px-3 rounded-lg border border-slate-200 text-sm flex-1 min-w-[160px]" />
        <select value={filters.classification} onChange={(e) => setFilters({ ...filters, classification: e.target.value })} data-testid="archived-classification" className="h-9 rounded-lg border border-slate-200 bg-white px-2 text-sm">
          <option value="">Todo escopo</option>{Object.entries(CLASSIFICATION_LABELS).map(([k, v]) => <option key={k} value={k}>{k} — {v}</option>)}
        </select>
        <select value={filters.sprint} onChange={(e) => setFilters({ ...filters, sprint: e.target.value })} data-testid="archived-sprint" className="h-9 rounded-lg border border-slate-200 bg-white px-2 text-sm">
          <option value="">Todas sprints</option><option value="none">Sem sprint</option>{(ctx.sprints || []).map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
        </select>
        <select value={filters.assignee} onChange={(e) => setFilters({ ...filters, assignee: e.target.value })} data-testid="archived-assignee" className="h-9 rounded-lg border border-slate-200 bg-white px-2 text-sm">
          <option value="">Todos</option><option value="me">Minhas</option><option value="none">Sem responsável</option>
        </select>
      </div>

      <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden">
        {tasks === null ? <div className="p-8 text-center text-sm text-slate-500">Carregando...</div>
          : tasks.length === 0 ? <div className="p-12 text-center text-sm text-slate-500" data-testid="archived-empty">Nenhuma tarefa arquivada.</div>
          : (
            <table className="w-full text-sm" data-testid="archived-list">
              <thead><tr className="text-left text-[11px] font-bold uppercase tracking-wider text-slate-500 border-b border-slate-200">
                <th className="px-5 py-3">Tarefa</th><th className="px-2 py-3 w-20">Escopo</th><th className="px-2 py-3 w-32">Responsável</th>
                <th className="px-2 py-3 w-28">Concluída</th><th className="px-2 py-3 w-28">Sprint</th><th className="px-2 py-3 w-28"></th>
              </tr></thead>
              <tbody className="divide-y divide-slate-100">
                {tasks.map((t) => (
                  <tr key={t.id} className="hover:bg-slate-50/60 cursor-pointer" data-testid={`archived-row-${t.id}`} onClick={() => ctx.openTask(t.id)}>
                    <td className="px-5 py-3 font-medium text-slate-900">{t.title}</td>
                    <td className="px-2 py-3"><span className={`inline-flex items-center justify-center w-7 h-7 rounded-md text-xs font-semibold ${CLASS_STYLE[t.classification]}`}>{t.classification}</span></td>
                    <td className="px-2 py-3 text-slate-600">{t.assignee_name || "—"}</td>
                    <td className="px-2 py-3 text-slate-600">{fmt(t.completed_at)}</td>
                    <td className="px-2 py-3 text-slate-600">{t.sprint_name || "—"}</td>
                    <td className="px-2 py-3 text-right">
                      {canAdmin && <button onClick={(e) => restore(t.id, e)} data-testid={`archived-restore-${t.id}`} className="inline-flex items-center gap-1 h-8 px-3 rounded-lg border border-slate-200 text-xs hover:bg-slate-50"><RotateCcw className="w-3.5 h-3.5" /> Restaurar</button>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
      </div>
    </div>
  );
}
